import { PlatformEnum } from 'constants-activate';
import { IntegrationDefinition } from 'lane-shared/types/integrations/IntegrationDefinition';
import {
  IntegrationCategoryEnum,
  IntegrationProviderEnum,
  IntegrationTypeEnum,
} from 'constants-integrations';
import { PackagedTypeEnum } from 'lane-shared/types/properties/PackagedTypeEnum';
import { Password } from '../../../properties/baseTypes';
import { PERMISSION_KEYS } from 'constants-permissions';
import { SecurityRuleTypeEnum } from 'lane-shared/types/properties/PropertySecurity';
import './types';

const BuildingEnginesPrismVisitorsDefinition: IntegrationDefinition = {
  name: IntegrationProviderEnum.BuildingEnginesPrismVisitors,
  hasCustomUI: true,
  friendlyName: 'BuildingEngines: Prism Visitors API',
  description:
    'Defines the properties required for a BuildingEngines Prism integration',
  category: IntegrationCategoryEnum.VisitorRegistration,
  platforms: [PlatformEnum.Web, PlatformEnum.Mobile],
  type: IntegrationTypeEnum.Api,
  settings: {
    baseUrl: {
      friendlyName: 'Base URL',
      description:
        'The base URL of the BuildingEngines Prism Visitors API endpoint',
      type: 'Url',
      default: 'https://api.connect.buildingengines.com',
      editable: false,
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
    },
  },
  properties: {
    subHeading: {
      type: String.name,
      validators: [],
      friendlyName: 'Enter your BE Prism credentials to load property names and IDs',
    },
    name: {
      friendlyName: 'Display Name',
      type: 'String',
      description:
        'Name that to be displayed on BuildingEngines Prism Visitors form',
      default: 'BuildingEngines Prism Visitors',
    },
    subtitle: {
      friendlyName: 'Subtitle',
      type: 'String',
      description: 'Subtitle for BuildingEngines Prism Visitors form',
      default: 'Tenant Service Request for Vistiors',
    },
    description: {
      friendlyName: 'Service Description',
      type: 'LongText',
      description:
        'Details for end user for BuildingEngines Prism Visitors form',
      default: 'Submit your BuildingEngines Prism Visitors request here.',
    },
    buildingId: {
      friendlyName: 'Building ID',
      type: 'String',
      description: 'Building ID that is stored in BuildingEngines system',
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
    },
    supportEmail: {
      friendlyName: 'Support contact Email',
      type: 'Email',
    },
    username: {
      type: String.name,
      friendlyName: 'Username',
      tags: ['metadataFieldRequest'],
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
      secure: {
        read: [
          {
            type: SecurityRuleTypeEnum.ChannelPermission,
            value: PERMISSION_KEYS.PERMISSION_INTEGRATION_READ,
          },
        ],
      },
    },
    password: {
      type: Password.name,
      friendlyName: 'Password',
      tags: ['metadataFieldRequest'],
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
      secure: {
        read: [
          {
            type: SecurityRuleTypeEnum.ChannelPermission,
            value: PERMISSION_KEYS.PERMISSION_INTEGRATION_READ,
          },
        ],
      },
    },
    loadSettingsButton: {
      friendlyName: 'Load Settings',
      type: String.name,
      validators: [],
    },
    buildingIds: {
      friendlyName: 'Select Buildings',
      packagedType: PackagedTypeEnum.MultiSelect,
      type: 'Option',
      description: 'Select Buildings that is stored in BuildingEngines system',
      isMultiSelect: true,
      isFullWidth: true,
      tags: ['metadataFieldResponse'],
      validators: [
        {
          name: 'ArrayMin',
          value: 1,
        },
        {
          name: 'In',
          value: [],
        },
      ],
    },
  },
  interactionData: {
    organizationId: {
      friendlyName: 'Organization',
      description: 'The unique id for the organization',
      type: 'String',
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
    },
    spaceId: {
      friendlyName: 'Floor/Space',
      description: 'The unique id for the location of this request.',
      type: 'String',
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
    },
    instructions: {
      friendlyName: 'Instructions for staff',
      description: 'Leave your instructions for staff',
      type: 'String',
    },
    startDate: {
      friendlyName: 'Start Date',
      description: 'Start date of the visit',
      type: 'String',
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
    },
    visitors: {
      friendlyName: 'Visitors',
      type: 'BEVisitor',
      isArray: true,
      default: [],
      validators: [
        {
          name: 'Required',
          value: true,
        },
        {
          name: 'ArrayMin',
          value: 1,
        },
      ],
    },
  },
};

/**
 * @deprecated Use `BuildingEnginesPrismVisitorsDefinition` from 'application-types' instead.
 * This definition has been moved to the application-types library for better modularity.
 * Import: `import { BuildingEnginesPrismVisitorsDefinition } from 'application-types';`
 */
export default BuildingEnginesPrismVisitorsDefinition;
